import { Fragment, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  CountrySelect,
  DatePicker,
  Field,
  Icon,
  Input,
  Modal,
  NumberInput,
  Select,
  Space,
  Tag,
  Text,
  Upload,
  useToast,
} from "@app/ui";
import type { GroupItemField, PassportField, PassportSchemaDef } from "@app/dpp-schemas";
import { importProductsCsv } from "../api/dpp.js";
import { ErrorAlert } from "../errors/index.js";
import { useLang, useT } from "../i18n.js";
import type { MessageKey } from "../i18n.js";
import { getSchemaDef, schemaOptions } from "./schema-registry.js";
import { formIssues, formsToCsv, groupCellKey, NAME_ID, readExcelForms, SKU_ID } from "./excel-import.js";
import type { ExcelForm, FieldIssue, GroupRow, OrphanRow } from "./excel-import.js";

/** Static workbooks built from the schemas by the template generator (apps/web/public/templates). */
function downloadTemplate(schemaId: string): void {
  const a = document.createElement("a");
  a.href = `/templates/${schemaId}.xlsx`;
  a.download = `${schemaId}.xlsx`;
  a.click();
}

/** A form under review; `fault` holds the server's rejection until the user edits the product. */
interface Draft extends ExcelForm {
  key: string;
  fault?: string | undefined;
}

const INVALID_MESSAGE: Partial<Record<PassportField["type"], MessageKey>> = {
  number: "excelImport.invalid.number",
  percentage: "excelImport.invalid.percentage",
  enum: "excelImport.invalid.enum",
  date: "excelImport.invalid.date",
  url: "excelImport.invalid.url",
  boolean: "excelImport.invalid.boolean",
};

/**
 * Excel data import, modelled on the 14064 quick import: download the
 * template, upload the filled workbook, review every product with its
 * missing/invalid fields flagged in place, fix them inline, then save. Only
 * complete products are sent (through the CSV import endpoint, so upsert-by-SKU,
 * plan caps and audit stay server-side); rejected ones stay in the list with
 * the server's message until fixed.
 */
export function ExcelImportModal({
  orgId,
  open,
  onClose,
  onSaved,
}: {
  orgId: string;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const { lang } = useLang();
  const toast = useToast();
  const options = useMemo(() => schemaOptions(lang), [lang]);
  const [templateId, setTemplateId] = useState(options[0]?.value ?? "");
  const [drafts, setDrafts] = useState<Draft[] | null>(null);
  const [orphans, setOrphans] = useState<OrphanRow[]>([]);
  // Products that needed attention at upload — open in the list, closed ones are already complete.
  const [initiallyOpen, setInitiallyOpen] = useState<string[]>([]);
  const [uploadSeq, setUploadSeq] = useState(0);
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);

  const issues = useMemo(() => {
    const map = new Map<string, Map<string, FieldIssue>>();
    if (!drafts) return map;
    const skuCount = new Map<string, number>();
    for (const d of drafts) {
      const sku = (d.values[SKU_ID] ?? "").trim();
      if (sku) skuCount.set(sku, (skuCount.get(sku) ?? 0) + 1);
    }
    for (const d of drafts) {
      const def = getSchemaDef(d.schemaId);
      const found = def ? formIssues(def, d.values, d.groups) : new Map<string, FieldIssue>();
      if ((skuCount.get((d.values[SKU_ID] ?? "").trim()) ?? 0) > 1) found.set(SKU_ID, "invalid");
      map.set(d.key, found);
    }
    return map;
  }, [drafts]);

  const isReady = (d: Draft) => !d.fault && getSchemaDef(d.schemaId) && issues.get(d.key)?.size === 0;
  const ready = drafts?.filter(isReady) ?? [];
  const toFix = (drafts?.length ?? 0) - ready.length;

  const close = () => {
    setDrafts(null);
    setError(null);
    onClose();
  };

  const onFiles = (files: File[]) => {
    const file = files[files.length - 1];
    if (!file) return;
    setError(null);
    void file
      .arrayBuffer()
      .then((buf) => readExcelForms(buf, getSchemaDef))
      .then(({ forms, orphans: unmatched }) => {
        if (forms.length === 0) {
          toast.error(t("import.xlsxEmpty"));
          return;
        }
        const next = forms.map((f, i) => ({ ...f, key: `${i}:${f.sheet}` }));
        setInitiallyOpen(
          next
            .filter((d) => {
              const def = getSchemaDef(d.schemaId);
              return !def || formIssues(def, d.values, d.groups).size > 0;
            })
            .map((d) => d.key),
        );
        setDrafts(next);
        setOrphans(unmatched);
        setUploadSeq((n) => n + 1);
      })
      .catch(setError);
  };

  const setValue = (key: string, id: string, value: string) =>
    setDrafts((prev) =>
      (prev ?? []).map((d) =>
        d.key === key ? { ...d, fault: undefined, values: { ...d.values, [id]: value } } : d,
      ),
    );

  const setGroup = (key: string, id: string, rows: GroupRow[]) =>
    setDrafts((prev) =>
      (prev ?? []).map((d) =>
        d.key === key ? { ...d, fault: undefined, groups: { ...d.groups, [id]: rows } } : d,
      ),
    );

  const remove = (key: string) => setDrafts((prev) => (prev ?? []).filter((d) => d.key !== key));

  const save = async () => {
    if (!drafts || ready.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const res = await importProductsCsv(orgId, formsToCsv(ready));
      // CSV line n+2 is ready[n]; rows the server rejected come back to the list with its reason.
      const rejected = new Map(res.errors.map((e) => [ready[e.row - 2]?.key, e.message]));
      const sent = new Set(ready.map((d) => d.key));
      const remaining = drafts
        .filter((d) => !sent.has(d.key) || rejected.has(d.key))
        .map((d) => (rejected.has(d.key) ? { ...d, fault: rejected.get(d.key) } : d));
      const savedCount = res.created + res.updated;
      if (savedCount > 0) {
        toast.success(t("excelImport.saved", { n: savedCount }));
        onSaved();
      }
      if (rejected.size > 0) toast.error(t("excelImport.rejected", { n: rejected.size }));
      if (remaining.length === 0) close();
      else setDrafts(remaining);
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  const errorText = (
    field: { type: string },
    issue: FieldIssue | undefined,
    id: string,
  ): string | undefined => {
    if (!issue) return undefined;
    if (issue === "required") return t("excelImport.required");
    if (id === SKU_ID) return t("excelImport.skuInvalid");
    if (id === NAME_ID) return t("excelImport.nameInvalid");
    return t(INVALID_MESSAGE[field.type as PassportField["type"]] ?? "excelImport.invalid.text");
  };

  const renderInput = (d: Draft, field: PassportField) => {
    const value = d.values[field.id] ?? "";
    const common = {
      label: field.labels[lang],
      required: field.required,
      ...(field.description ? { help: field.description } : {}),
    };
    const err = errorText(field, issues.get(d.key)?.get(field.id), field.id);
    const withError = err ? { error: err } : {};
    const onText = (v: string) => setValue(d.key, field.id, v);
    switch (field.type) {
      case "number":
      case "percentage":
        return (
          <NumberInput
            {...common}
            {...withError}
            value={value}
            onChange={onText}
            {...(field.type === "percentage" ? { min: 0, max: 100 } : {})}
            {...(field.unit ? { suffix: field.unit } : {})}
          />
        );
      case "boolean":
        // Three-way select: an empty cell must stay "not provided", not silently become "no".
        return (
          <Select
            {...common}
            {...withError}
            options={[
              { label: "—", value: "" },
              { label: t("common.yes"), value: "true" },
              { label: t("common.no"), value: "false" },
            ]}
            value={value}
            onChange={onText}
          />
        );
      case "enum":
        return (
          <Select
            {...common}
            {...withError}
            options={(field.enumValues ?? []).map((v) => ({ label: v, value: v }))}
            value={(field.enumValues ?? []).includes(value) ? value : ""}
            onChange={onText}
          />
        );
      case "country":
        return <CountrySelect {...common} {...withError} allowClear value={value} onChange={onText} />;
      case "date":
        return <DatePicker {...common} {...withError} allowClear value={value} onChange={onText} />;
      default:
        return (
          <Input
            {...common}
            {...withError}
            {...(field.type === "url" ? { placeholder: "https://…" } : {})}
            {...(field.unit ? { suffix: field.unit } : {})}
            value={value}
            onChange={(e) => onText(e.target.value)}
          />
        );
    }
  };

  /** One compact control per list column, as on the product page; labels ride as placeholders. */
  const renderGroupCell = (d: Draft, field: PassportField, item: GroupItemField, idx: number) => {
    const rows = d.groups[field.id] ?? [];
    const v = rows[idx]?.[item.key] ?? "";
    const placeholder = item.labels[lang];
    const err = errorText(item, issues.get(d.key)?.get(groupCellKey(field.id, idx, item.key)), item.key);
    const withError = err ? { error: err } : {};
    const onText = (nv: string) =>
      setGroup(
        d.key,
        field.id,
        rows.map((r, i) => (i === idx ? { ...r, [item.key]: nv } : r)),
      );
    switch (item.type) {
      case "number":
      case "percentage":
        return (
          <NumberInput
            key={item.key}
            placeholder={placeholder}
            {...withError}
            value={v}
            onChange={onText}
            {...(item.type === "percentage" ? { min: 0, max: 100 } : {})}
            {...(item.unit ? { suffix: item.unit } : {})}
          />
        );
      case "boolean":
        return (
          <Select
            key={item.key}
            placeholder={placeholder}
            {...withError}
            options={[
              { label: "—", value: "" },
              { label: t("common.yes"), value: "true" },
              { label: t("common.no"), value: "false" },
            ]}
            value={v}
            onChange={onText}
          />
        );
      case "enum":
        return (
          <Select
            key={item.key}
            placeholder={placeholder}
            {...withError}
            options={(item.enumValues ?? []).map((e) => ({ label: e, value: e }))}
            value={(item.enumValues ?? []).includes(v) ? v : ""}
            onChange={onText}
          />
        );
      case "country":
        return (
          <CountrySelect
            key={item.key}
            placeholder={placeholder}
            {...withError}
            allowClear
            value={v}
            onChange={onText}
          />
        );
      case "date":
        return <DatePicker key={item.key} {...withError} allowClear value={v} onChange={onText} />;
      default:
        return (
          <Input
            key={item.key}
            placeholder={placeholder}
            {...withError}
            value={v}
            onChange={(e) => onText(e.target.value)}
          />
        );
    }
  };

  const renderGroup = (d: Draft, field: PassportField) => {
    const rows = d.groups[field.id] ?? [];
    const items = field.itemFields ?? [];
    return (
      <Field
        key={field.id}
        label={field.labels[lang]}
        required={field.required}
        {...(field.description ? { help: field.description } : {})}
      >
        <div className="pd-group">
          <div
            className="pd-group-grid"
            style={{ gridTemplateColumns: `repeat(${items.length}, minmax(150px, 1fr)) 32px` }}
          >
            {items.map((item) => (
              <div key={item.key} className="pd-group-hcell">
                {item.labels[lang]}
                {item.unit ? ` (${item.unit})` : ""}
                {item.required ? " *" : ""}
              </div>
            ))}
            <div />
            {rows.map((_, idx) => (
              <Fragment key={idx}>
                {items.map((item) => renderGroupCell(d, field, item, idx))}
                <Button
                  size="sm"
                  variant="subtle"
                  aria-label={t("common.remove")}
                  icon={<Icon name="trash" size={14} />}
                  onClick={() =>
                    setGroup(
                      d.key,
                      field.id,
                      rows.filter((__, i) => i !== idx),
                    )
                  }
                />
              </Fragment>
            ))}
          </div>
          <div className="pd-group-foot">
            <Button
              size="sm"
              icon={<Icon name="plus" size={14} />}
              onClick={() => setGroup(d.key, field.id, [...rows, {}])}
            >
              {t("product.addRow")}
            </Button>
          </div>
        </div>
      </Field>
    );
  };

  const renderProduct = (d: Draft, def: PassportSchemaDef | undefined) => {
    const found = issues.get(d.key);
    const productField = (id: string, label: string) => {
      const err = errorText({ type: "string" }, found?.get(id), id);
      return (
        <Input
          label={label}
          required
          {...(err ? { error: err } : {})}
          value={d.values[id] ?? ""}
          onChange={(e) => setValue(d.key, id, e.target.value)}
        />
      );
    };
    return (
      <Space direction="vertical" size="md" style={{ width: "100%" }}>
        {d.fault && <Alert type="error">{d.fault}</Alert>}
        {!def && <Alert type="error">{t("excelImport.unknownSchema", { id: d.schemaId })}</Alert>}
        <div className="pd-field-grid">
          {productField(SKU_ID, t("excelImport.sku"))}
          {productField(NAME_ID, t("excelImport.name"))}
        </div>
        {def?.categories.map((category) => {
          // Required fields always; optional ones only when the workbook carried a value for them.
          const fields = category.fields.filter(
            (f) => f.type !== "group" && (f.required || (d.values[f.id] ?? "") !== ""),
          );
          const lists = category.fields.filter(
            (f) => f.type === "group" && (f.required || (d.groups[f.id] ?? []).length > 0),
          );
          if (fields.length === 0 && lists.length === 0) return null;
          return (
            <div key={category.id}>
              <Text strong>{category.labels[lang]}</Text>
              <div className="pd-field-grid" style={{ marginTop: "var(--az-sp-2)" }}>
                {fields.map((f) => (
                  <div key={f.id}>{renderInput(d, f)}</div>
                ))}
              </div>
              {lists.map((f) => (
                <div key={f.id} style={{ marginTop: "var(--az-sp-2)" }}>
                  {renderGroup(d, f)}
                </div>
              ))}
            </div>
          );
        })}
        <div>
          <Button
            size="sm"
            variant="subtle"
            icon={<Icon name="trash" size={14} />}
            onClick={() => remove(d.key)}
          >
            {t("excelImport.remove")}
          </Button>
        </div>
      </Space>
    );
  };

  const statusTag = (d: Draft) => {
    if (d.fault) return <Tag color="error">{t("excelImport.status.fault")}</Tag>;
    const n = issues.get(d.key)?.size ?? 0;
    if (n > 0 || !getSchemaDef(d.schemaId))
      return <Tag color="warning">{t("excelImport.status.issues", { n })}</Tag>;
    return <Tag color="success">{t("excelImport.status.ready")}</Tag>;
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={t("excelImport.title")}
      width={drafts ? 1080 : 620}
      footer={
        drafts ? (
          <>
            <Button variant="default" onClick={() => setDrafts(null)} disabled={saving}>
              {t("excelImport.back")}
            </Button>
            <Button
              variant="primary"
              icon={<Icon name="upload" size={14} />}
              loading={saving}
              disabled={ready.length === 0}
              onClick={() => void save()}
            >
              {t("excelImport.save", { n: ready.length })}
            </Button>
          </>
        ) : null
      }
    >
      {!drafts ? (
        <Space direction="vertical" size="md" style={{ width: "100%" }}>
          <Alert type="info">{t("excelImport.steps")}</Alert>
          <div className="pd-field-grid" style={{ alignItems: "end" }}>
            <Select
              label={t("excelImport.template")}
              options={options}
              value={templateId}
              onChange={setTemplateId}
            />
            <Button icon={<Icon name="download" size={14} />} onClick={() => downloadTemplate(templateId)}>
              {t("excelImport.download")}
            </Button>
          </div>
          <Upload dropzone accept=".xlsx" maxCount={1} onChange={onFiles}>
            {t("excelImport.upload")}
          </Upload>
          {error != null && <ErrorAlert error={error} />}
        </Space>
      ) : (
        <Space direction="vertical" size="md" style={{ width: "100%" }}>
          <Space size="sm" wrap>
            <Tag color={toFix > 0 ? "warning" : "default"}>{t("excelImport.toFix", { n: toFix })}</Tag>
            <Tag color={ready.length > 0 ? "success" : "default"}>
              {t("excelImport.ready", { n: ready.length })}
            </Tag>
          </Space>
          <Alert type={toFix > 0 ? "warning" : "success"}>
            {toFix > 0 ? t("excelImport.fixHint") : t("excelImport.allReady")} {t("excelImport.updateNote")}
          </Alert>
          {orphans.length > 0 && (
            <Alert type="warning">
              {t("excelImport.orphans", {
                n: orphans.length,
                rows: orphans
                  .slice(0, 5)
                  .map((o) => `${o.sheet} ${o.row}${o.sku ? ` (${o.sku})` : ""}`)
                  .join(", "),
              })}
            </Alert>
          )}
          {error != null && <ErrorAlert error={error} onRetry={() => void save()} />}
          <Collapse
            // Re-mount per upload so the open/closed defaults follow the new file.
            key={uploadSeq}
            defaultActiveKeys={initiallyOpen}
            items={drafts.map((d) => ({
              key: d.key,
              label: (
                <Space size="sm" wrap>
                  <Text strong>{d.sheet}</Text>
                  <Text secondary>
                    {(d.values[SKU_ID] ?? "").trim() || "—"} · {(d.values[NAME_ID] ?? "").trim() || "—"}
                  </Text>
                  {statusTag(d)}
                </Space>
              ),
              children: renderProduct(d, getSchemaDef(d.schemaId)),
            }))}
          />
        </Space>
      )}
    </Modal>
  );
}
